import { useState, useEffect } from "react";

import { ChevronDownIcon, PencilSquareIcon } from "@heroicons/react/24/outline";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { useAuth } from "../contexts/AuthContext";
import apiService from "../services/api";
import {
  FAQ_PLATFORMS,
  PLATFORM_PILL,
  categoryMatchesPlatform,
  platformLabel,
} from "../utils/faqPlatforms";

const FAQ = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeIndex, setActiveIndex] = useState(null);
  const [faqData, setFaqData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [platformFilter, setPlatformFilter] = useState("all");

  useEffect(() => {
    const fetchFAQs = async () => {
      try {
        setLoading(true);
        const response = await apiService.getFAQs(true);

        if (response.status === 200 && response.data) {
          setFaqData(response.data);
        }
      } catch (error) {
        toast.error("Failed to load FAQs");
      } finally {
        setLoading(false);
      }
    };

    fetchFAQs();
  }, []);

  const toggleAccordion = (index) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  if (loading) {
    return (
      <div className="p-4 md:p-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex justify-center items-center h-64">
            <div className="text-gray-500">Loading FAQs...</div>
          </div>
        </div>
      </div>
    );
  }

  const visibleCategories = faqData.filter((category) =>
    categoryMatchesPlatform(
      category.questions?.[0]?.metadata?.platforms || [],
      platformFilter
    )
  );

  return (
    <div className="p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        <motion.div
          animate={{ opacity: 1 }}
          className="mb-8"
          initial={{ opacity: 0 }}
        >
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Frequently Asked Questions
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Find answers to common questions about Lunova Player
              </p>
            </div>
            {user?.role === "admin" && (
              <button
                className="btn btn-primary flex items-center gap-2"
                onClick={() => navigate("/faq-manager")}
              >
                <PencilSquareIcon className="w-5 h-5" />
                Edit FAQ
              </button>
            )}
          </div>
        </motion.div>

        {/* Platform filter */}
        <div className="flex items-center flex-wrap gap-2 mb-6">
          <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
            Platform:
          </span>
          {[{ key: "all", label: "All" }, ...FAQ_PLATFORMS].map((p) => (
            <button
              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                platformFilter === p.key
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
              }`}
              key={p.key}
              onClick={() => setPlatformFilter(p.key)}
            >
              {p.label}
            </button>
          ))}
        </div>

        <motion.div
          animate={{ opacity: 1 }}
          className="space-y-6"
          initial={{ opacity: 0 }}
          transition={{ delay: 0.2 }}
        >
          {faqData.map((category, categoryIndex) => {
            const baseIndex = faqData
              .slice(0, categoryIndex)
              .reduce((acc, cat) => acc + cat.questions.length, 0);
            const catPlatforms =
              category.questions?.[0]?.metadata?.platforms || [];

            if (!categoryMatchesPlatform(catPlatforms, platformFilter)) {
              return null;
            }

            return (
              <div className="space-y-3" key={categoryIndex}>
                <div className="flex items-center flex-wrap gap-2 mb-4 px-2">
                  <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
                    {category.category}
                  </h3>
                  {catPlatforms.map((key) => (
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-medium ${PLATFORM_PILL[key]}`}
                      key={key}
                    >
                      {platformLabel(key)}
                    </span>
                  ))}
                </div>
                {category.questions.map((item, questionIndex) => {
                  const currentIndex = baseIndex + questionIndex;
                  const isActive = activeIndex === currentIndex;

                  return (
                    <motion.div
                      animate={{ opacity: 1 }}
                      className="bg-white/80 dark:bg-gray-800/60 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 rounded-xl shadow-md hover:shadow-lg transition-all duration-300"
                      initial={{ opacity: 0 }}
                      key={currentIndex}
                      transition={{ delay: currentIndex * 0.05 }}
                    >
                      <button
                        className="w-full px-6 py-4 flex items-center justify-between text-left focus:outline-none rounded-xl bg-gray-200/70 hover:bg-gray-300/80 dark:bg-gray-800/70 dark:hover:bg-gray-800/80 transition-colors"
                        onClick={() => toggleAccordion(currentIndex)}
                      >
                        <span
                          className="font-medium text-gray-900 dark:text-white pr-4 faq-question"
                          dangerouslySetInnerHTML={{ __html: item.q }}
                        />
                        <motion.div
                          animate={{ rotate: isActive ? 180 : 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          <ChevronDownIcon className="w-5 h-5 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                        </motion.div>
                      </button>

                      <AnimatePresence>
                        {isActive && (
                          <motion.div
                            animate={{ height: "auto", opacity: 1 }}
                            className="overflow-hidden"
                            exit={{ height: 0, opacity: 0 }}
                            initial={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3, ease: "easeInOut" }}
                          >
                            <div
                              className="px-6 pt-5 pb-4 text-gray-600 dark:text-gray-300 bg-gray-100/30 dark:bg-gray-700/30 backdrop-blur-sm rounded-b-xl faq-content"
                              dangerouslySetInnerHTML={{ __html: item.a }}
                            />
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  );
                })}
              </div>
            );
          })}

          {visibleCategories.length === 0 && (
            <div className="text-center text-gray-500 dark:text-gray-400 py-8">
              No FAQs for this platform yet.
            </div>
          )}
        </motion.div>

        <motion.div
          animate={{ opacity: 1 }}
          className="mt-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl p-8 text-white text-center"
          initial={{ opacity: 0 }}
          transition={{ delay: 0.5 }}
        >
          <h3 className="text-2xl font-bold mb-4">Still need help?</h3>
          <p className="mb-6">
            Our customer support team is here to assist you
          </p>
          <button
            className="bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-300"
            onClick={() => navigate("/tickets")}
          >
            Open Support Ticket
          </button>
        </motion.div>
      </div>
    </div>
  );
};

export default FAQ;
